import { Box, Tooltip as MuiTooltip, Typography } from "@mui/material";
import { createCurrencyFormatter } from "utils/currency-utils";
import { ChartContainer } from "../../../../widgets/charts";
import { salesCreditLimitChartConfig } from "./chart-configs";

interface CreditLimitData {
  used: number;
  remaining: number;
  total: number;
}

interface Props {
  data: CreditLimitData;
  title?: string;
  currencyFormatter?: {
    format(val: number): string;
  };
}

// Default currency formatter with no decimal places
const defaultCurrencyFormatter = createCurrencyFormatter("GBP")({
  rounding: {
    kind: "rounded",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  },
});

export const SalesCreditLimitChart = ({
  data,
  title,
  currencyFormatter,
}: Props) => {
  const { total, used, remaining } = data;
  const usedPercentage = total > 0 ? (used / total) * 100 : 0;
  const legends = salesCreditLimitChartConfig.legends;
  const progressColors = salesCreditLimitChartConfig.progressColors;

  const formatCurrency = (value: number) => {
    if (currencyFormatter) {
      return currencyFormatter.format(value);
    }
    return defaultCurrencyFormatter.format(value);
  };

  const tooltipProps = {
    arrow: true,
    placement: "top" as const,
    componentsProps: {
      tooltip: {
        sx: {
          bgcolor: "white",
          color: "#000",
          border: "1px solid #ccc",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          "& .MuiTooltip-arrow": {
            color: "white",
            "&::before": {
              border: "1px solid #ccc",
            },
          },
        },
      },
    },
  };

  const progressBarTooltip = (
    <Box sx={{ p: 1 }}>
      <Typography
        variant="body2"
        sx={{ mb: 0.5, color: "#333", fontWeight: 600 }}
      >
        Used: {formatCurrency(used)}
      </Typography>
      <Typography
        variant="body2"
        sx={{ mb: 0.5, color: "#333", fontWeight: 600 }}
      >
        Remaining: {formatCurrency(remaining)}
      </Typography>
      <Typography variant="body2" sx={{ color: "#333", fontWeight: 600 }}>
        Total Credit Limit: {formatCurrency(total)}
      </Typography>
    </Box>
  );

  return (
    <ChartContainer
      title={title || salesCreditLimitChartConfig.title}
      titleTooltip={salesCreditLimitChartConfig.titleTooltip}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Custom progress bar with tooltip */}
        <Box sx={{ mb: 2, width: "80%", maxWidth: 300 }}>
          <MuiTooltip title={progressBarTooltip} {...tooltipProps}>
            <Box
              sx={{
                width: "100%",
                height: 18,
                backgroundColor: progressColors.remaining,
                borderRadius: 4,
                position: "relative",
                overflow: "hidden",
                cursor: "pointer",
              }}
            >
              <Box
                sx={{
                  width: `${usedPercentage}%`,
                  height: "100%",
                  backgroundColor: progressColors.used,
                  borderRadius: usedPercentage === 100 ? 4 : "16px 0 0 16px",
                  transition: "width 0.3s ease",
                }}
              />
            </Box>
          </MuiTooltip>
        </Box>

        {/* Bottom legend with colored dots */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 4,
            mt: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {legends.map((legend) => (
            <MuiTooltip
              key={legend.key}
              title={legend.tooltip}
              {...tooltipProps}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    backgroundColor: legend.color,
                    borderRadius: "50%",
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontWeight: 500 }}
                >
                  {legend.label}
                </Typography>
              </Box>
            </MuiTooltip>
          ))}
        </Box>
      </Box>
    </ChartContainer>
  );
};
